import React, { useMemo } from "react";
import { Marker, Popup } from "react-leaflet";
import * as L from "leaflet";
import type { CommunityPartnerMapItem } from "../../../types/CommunityImpact";

const HOUSE_ICON_SVG = `
<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M10 14V10.6667C10 10.4899 9.92976 10.3203 9.80474 10.1953C9.67971 10.0702 9.51014 10 9.33333 10H6.66667C6.48986 10 6.32029 10.0702 6.19526 10.1953C6.07024 10.3203 6 10.4899 6 10.6667V14" stroke="white" stroke-width="1.33" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M11.8491 6.87325C11.7102 6.74021 11.5252 6.66595 11.3328 6.66595C11.1404 6.66595 10.9554 6.74021 10.8165 6.87325C10.5065 7.16893 10.0945 7.33389 9.66614 7.33389C9.23774 7.33389 8.8258 7.16893 8.51581 6.87325C8.37686 6.74041 8.19204 6.66627 7.99981 6.66627C7.80757 6.66627 7.62275 6.74041 7.4838 6.87325C7.17378 7.16913 6.76169 7.33421 6.33314 7.33421C5.90458 7.33421 5.4925 7.16913 5.18247 6.87325C5.0435 6.74021 4.85853 6.66595 4.66614 6.66595C4.47375 6.66595 4.28878 6.74021 4.14981 6.87325C3.85037 7.15899 3.45532 7.3231 3.04156 7.33362C2.62779 7.34414 2.22492 7.20033 1.91134 6.93017C1.59777 6.66002 1.39594 6.28285 1.34513 5.87208C1.29432 5.46131 1.39817 5.04633 1.63647 4.70792L3.56247 1.91859C3.68468 1.73826 3.8492 1.59062 4.04166 1.48858C4.23411 1.38654 4.44864 1.33321 4.66647 1.33325H11.3331C11.5503 1.33317 11.7643 1.38615 11.9563 1.48758C12.1484 1.58901 12.3127 1.73582 12.4351 1.91525L14.3651 4.70992C14.6035 5.0486 14.7072 5.4639 14.6561 5.87488C14.605 6.28586 14.4027 6.66308 14.0886 6.93305C13.7745 7.20301 13.3712 7.34637 12.9572 7.33519C12.5432 7.32401 12.1482 7.1591 11.8491 6.87259" stroke="white" stroke-width="1.33" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M2.6665 7.30005V12.6667C2.6665 13.0203 2.80698 13.3595 3.05703 13.6095C3.30708 13.8596 3.64622 14 3.99984 14H11.9998C12.3535 14 12.6926 13.8596 12.9426 13.6095C13.1927 13.3595 13.3332 13.0203 13.3332 12.6667V7.30005" stroke="white" stroke-width="1.33" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`;

const communityPartnerIcon = L.divIcon({
  html: `
    <div class="community-marker community-marker--strong">
      ${HOUSE_ICON_SVG}
    </div>
  `,
  className: "",
  iconSize: [35, 35],
  iconAnchor: [17.5, 17.5],
  popupAnchor: [0, -17.5],
});

type CommunityPartnerMarkerLayerProps = {
  communityPartners: CommunityPartnerMapItem[];
};

export default function CommunityPartnerMarkerLayer({
  communityPartners,
}: CommunityPartnerMarkerLayerProps) {
  const validPartners = useMemo(
    () =>
      communityPartners.filter(
        (partner) =>
          typeof partner.latitude === "number" &&
          !Number.isNaN(partner.latitude) &&
          typeof partner.longitude === "number" &&
          !Number.isNaN(partner.longitude),
      ),
    [communityPartners],
  );

  return (
    <>
      {validPartners.map((partner) => (
        <Marker
          key={partner.id}
          position={[partner.latitude as number, partner.longitude as number]}
          icon={communityPartnerIcon}
        >
          <Popup>
            <div className="space-y-1 text-sm">
              <div className="font-semibold text-[#00BCD4] dark:text-cyan-400">
                {partner.name}
              </div>
              <div className="text-[#4A5565] dark:text-gray-300">
                {partner.address || "-"}
              </div>
              <div className="text-[#4A5565] dark:text-gray-300">
                Email: {partner.email || "-"}
              </div>
              <div className="text-[#4A5565] dark:text-gray-300 break-all">
                Website:{" "}
                {partner.website ? (
                  <a
                    href={partner.website}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#00BCD4] dark:text-cyan-400 hover:underline"
                  >
                    {partner.website}
                  </a>
                ) : (
                  "-"
                )}
              </div>
              <div className="text-[#4A5565] dark:text-gray-300">
                Type: {partner.type || "-"}
              </div>
            </div>
          </Popup>
        </Marker>
      ))}
    </>
  );
}
